import { useCallback, useEffect, useState } from "react";

import { PortalCommunicationComposite } from "@/Common/Models/PortalCommunication/PortalCommunicationComposite";
import { CommunicationsUIStateInbox } from "@/Common/Models/UI/Enums";
import { PortalCommunicationsClient } from "@/Communications/Api/Dto/PortalCommunicationsClient";
import { PortalCommunicationFilterSettingsType } from "@/Communications/Models/PortalCommunication/PortalCommunicationFilterSettings";
import { PortalCommunicationTableSetting } from "@/Communications/Models/PortalCommunication/PortalCommunicationTableSetting";

interface IUsePortalCommunicationTableDataResponse {
    data: PortalCommunicationComposite[];
    sharedItemIds: string[];
    totalCount: number;
    fetchPortalCommunications: () => Promise<void>;
}

function usePortalCommunicationTableData(
    tableSetting: PortalCommunicationTableSetting,
    filterSetting: PortalCommunicationFilterSettingsType,
    inboxType: CommunicationsUIStateInbox
): IUsePortalCommunicationTableDataResponse {
    const [data, setData] = useState<PortalCommunicationComposite[]>([]);
    const [sharedItemIds, setSharedItemIds] = useState<string[]>([]);
    const [totalCount, setTotalCount] = useState(0);

    const fetchPortalCommunications = useCallback(
        async (): Promise<void> => {
            const settingKey = inboxType === CommunicationsUIStateInbox.Sent ? "SentFilterSetting" : "InboxFilterSetting";

            const response = await PortalCommunicationsClient.fetchPortalCommunications({
                InboxType: inboxType,
                [settingKey]: filterSetting,
                SearchText: tableSetting.searchText,
                CurrentPageNumber: tableSetting.currentPageNumber,
                QueryOrderDirection: tableSetting.queryOrderDirection,
                QueryOrderType: tableSetting.queryOrderType
            });

            if (response) {
                setData(response.portalCommunicationComposites);
                setSharedItemIds(response.portalCommunicationSharedItemIds);
                setTotalCount(response.totalCount);
            }
            else {
                setData([]);
                setSharedItemIds([]);
                setTotalCount(0);
            }
        },
        [
            inboxType,
            filterSetting,
            tableSetting.searchText,
            tableSetting.currentPageNumber,
            tableSetting.queryOrderDirection,
            tableSetting.queryOrderType
        ]
    );

    useEffect(
        () => {
            fetchPortalCommunications();
        },
        [fetchPortalCommunications]
    );

    return { data, sharedItemIds, totalCount, fetchPortalCommunications };
}

export { usePortalCommunicationTableData };
